import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    VStack,
    HStack,
    Text,
    Button,
    Avatar,
    Box,
    Circle,
    Icon,
    IconButton,
    Center,
    Badge,
    Spinner,
    Progress,
    Grid,
    GridItem,
    Tooltip,
} from '@chakra-ui/react';
import React, { Suspense } from 'react';
import { ChatViewBaseConfig } from '../../../base';
import { CallRequestOverlayViewProps } from './callRequestOverlayProps';
import { AiOutlineClose } from 'react-icons/ai';
import { FiMic, FiPhoneOff, FiVideo, FiMoreVertical } from 'react-icons/fi';
import { HiSpeakerWave, HiSignal } from 'react-icons/hi2';
import { CallRequestNewContainerProps, CallRequestNewViewProps } from '../new';
import { CallRequestDetailViewProps } from '../detail';
import { CallRequestUpsertComponents, CallRequestUpsertContainerProps } from '../upsert';

// Simple status display component for non-connected states
const StatusDisplay: React.FC<
    Pick<CallRequestNewViewProps | CallRequestDetailViewProps, 'status'> & {
        showSpinner?: boolean;
        children?: React.ReactNode;
    }
> = ({ status, showSpinner = false, children }) => (
    <VStack spacing={6} align="center" justify="center" h="300px">
        <Circle size="60px" bg={`${status.colorScheme}.500`} color="white">
            <Icon as={status.Icon} boxSize={6} />
        </Circle>

        <VStack spacing={2} align="center">
            <Text fontSize="lg" fontWeight="semibold" color="white">
                {status.label}
            </Text>
            <Text fontSize="sm" color="whiteAlpha.700">
                {status.description}
            </Text>
        </VStack>

        {showSpinner && <Spinner size="md" color={`${status.colorScheme}.500`} />}

        <Badge
            colorScheme={status.colorScheme}
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
            variant="solid"
        >
            {status.label}
        </Badge>

        {children}
    </VStack>
);

// Call UI component for connecting state
const CallConnectingView: React.FC<CallRequestNewViewProps> = (props) => {
    return <StatusDisplay status={props.status} showSpinner={true} />;
};

// Call UI component for connected state
const CallConnectedView: React.FC<CallRequestDetailViewProps> = (props) => {
    const [connectionQuality, setConnectionQuality] = React.useState<
        'excellent' | 'good' | 'poor'
    >('excellent');

    const getConnectionColor = () => {
        switch (connectionQuality) {
            case 'excellent':
                return 'green.400';
            case 'good':
                return 'yellow.400';
            case 'poor':
                return 'red.400';
            default:
                return 'gray.400';
        }
    };

    // Simulate connection quality changes for connected state
    React.useEffect(() => {
        if (props.status.kind === 'connected') {
            const interval = setInterval(() => {
                const qualities: ('excellent' | 'good' | 'poor')[] = [
                    'excellent',
                    'good',
                    'poor',
                ];
                setConnectionQuality(
                    qualities[Math.floor(Math.random() * qualities.length)]
                );
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [props.status.kind]);

    // Render connecting state
    if (props.status.kind === 'connecting') {
        return <StatusDisplay status={props.status} showSpinner={true} />;
    }

    // Render connected state
    if (props.status.kind === 'connected') {
        return (
            <VStack spacing={6} py={6} px={4} h="full" color="white">
                {/* Header with Call Status and Connection Quality */}
                <HStack justify="space-between" w="full" align="center">
                    <VStack spacing={1} align="start">
                        <HStack spacing={2}>
                            <Badge
                                colorScheme={props.status.colorScheme}
                                fontSize="sm"
                                px={3}
                                py={1}
                                borderRadius="full"
                                variant="solid"
                            >
                                <Text>{props.status.label}</Text>
                            </Badge>
                            <Tooltip
                                label={`Connection: ${connectionQuality}`}
                                placement="top"
                            >
                                <Circle size="20px" bg={getConnectionColor()} p={1}>
                                    <Icon
                                        as={props.status.Icon}
                                        boxSize={3}
                                        color="white"
                                    />
                                </Circle>
                            </Tooltip>
                        </HStack>
                    </VStack>

                    <IconButton
                        aria-label="More options"
                        icon={<Icon as={FiMoreVertical} />}
                        size="sm"
                        variant="ghost"
                        color="whiteAlpha.700"
                        _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
                    />
                </HStack>

                {/* Main Participant Area */}
                <VStack spacing={4} flex={1} justify="center">
                    <Grid templateColumns="1fr" gap={4} w="full">
                        {props.participants.map((participant, index) => (
                            <GridItem key={participant.kind}>
                                <VStack spacing={3}>
                                    <Box position="relative">
                                        <Avatar
                                            size={
                                                participant.kind === 'assistant'
                                                    ? '2xl'
                                                    : 'xl'
                                            }
                                            name={participant.label}
                                            src={participant.avatar}
                                            border={
                                                participant.speaking
                                                    ? '4px solid'
                                                    : '2px solid'
                                            }
                                            borderColor={
                                                participant.speaking
                                                    ? 'green.400'
                                                    : 'whiteAlpha.300'
                                            }
                                            transition="all 0.3s ease"
                                        />
                                        {/* Speaking indicator */}
                                        {participant.speaking && (
                                            <Box
                                                position="absolute"
                                                top="-2px"
                                                right="-2px"
                                                bg="green.400"
                                                borderRadius="full"
                                                p={1}
                                                animation="pulse 1s infinite"
                                            >
                                                <Icon
                                                    as={HiSpeakerWave}
                                                    boxSize={3}
                                                    color="white"
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                    <VStack spacing={1}>
                                        <Text
                                            fontSize="md"
                                            fontWeight="medium"
                                            color="white"
                                        >
                                            {participant.label}
                                        </Text>
                                        <Text fontSize="xs" color="whiteAlpha.700">
                                            {participant.kind === 'assistant'
                                                ? 'AI Assistant'
                                                : 'Current User'}
                                        </Text>
                                    </VStack>
                                </VStack>
                            </GridItem>
                        ))}
                    </Grid>
                </VStack>

                {/* Call Controls */}
                <HStack spacing={8} justify="center">
                    <Tooltip label="Mute" placement="top">
                        <IconButton
                            aria-label="Mute"
                            icon={<Icon as={FiMic} boxSize={6} />}
                            size="lg"
                            borderRadius="full"
                            colorScheme="gray"
                            variant="outline"
                            color="white"
                            borderColor="whiteAlpha.300"
                            _hover={{
                                transform: 'scale(1.1)',
                                borderColor: 'whiteAlpha.500',
                            }}
                            transition="all 0.2s"
                            w={16}
                            h={16}
                        />
                    </Tooltip>

                    <Tooltip label="Video" placement="top">
                        <IconButton
                            aria-label="Video"
                            icon={<Icon as={FiVideo} boxSize={6} />}
                            size="lg"
                            borderRadius="full"
                            colorScheme="gray"
                            variant="outline"
                            color="white"
                            borderColor="whiteAlpha.300"
                            _hover={{
                                transform: 'scale(1.1)',
                                borderColor: 'whiteAlpha.500',
                            }}
                            transition="all 0.2s"
                            w={16}
                            h={16}
                        />
                    </Tooltip>

                    <Tooltip label="End call" placement="top">
                        <IconButton
                            {...props.end.button}
                            aria-label="Hang up"
                            icon={<Icon as={FiPhoneOff} boxSize={6} />}
                            size="lg"
                            borderRadius="full"
                            colorScheme="red"
                            variant="solid"
                            _hover={{
                                transform: 'scale(1.1)',
                                bg: 'red.600',
                            }}
                            transition="all 0.2s"
                            w={16}
                            h={16}
                        />
                    </Tooltip>
                </HStack>
            </VStack>
        );
    }

    // Render ended state
    if (props.status.kind === 'ended') {
        return <StatusDisplay status={props.status} />;
    }

    // Render error state
    if (props.status.kind === 'error') {
        return <StatusDisplay status={props.status} />;
    }

    // Default/fallback state
    return <StatusDisplay status={props.status} showSpinner={true} />;
};

export function createCallRequestOverlayView(
    config: ChatViewBaseConfig & {
        Container: {
            Call: {
                Upsert: React.FC<CallRequestUpsertContainerProps>;
            };
        };
    }
): React.FC<CallRequestOverlayViewProps> {
    const {
        Container: {
            Call: { Upsert: CallRequestUpsertContainer },
        },
    } = config;

    const Components: CallRequestUpsertComponents = {
        New: CallConnectingView,
        Detail: CallConnectedView,
    };

    return (props) => {
        // Only allow closing through the end call button
        const handleClose = () => {};

        return (
            <Modal
                isOpen={props.isOpen}
                onClose={handleClose}
                size="xl"
                isCentered
                closeOnOverlayClick={false}
                returnFocusOnClose={false}
                closeOnEsc={false}
            >
                <ModalOverlay bg="blackAlpha.700" />
                <ModalContent
                    bg="gray.900"
                    border="1px"
                    borderColor="whiteAlpha.200"
                    color="white"
                    maxW="600px"
                    minH="500px"
                >
                    <ModalHeader pb={2}>
                        <HStack justify="space-between" align="center" w="full">
                            <Text fontSize="lg" fontWeight="semibold">
                                {props.title}
                            </Text>
                            <Text
                                fontSize="md"
                                color="whiteAlpha.700"
                                fontWeight="medium"
                            >
                                {props.callDuration}
                            </Text>
                        </HStack>
                    </ModalHeader>
                    <ModalBody pb={6}>
                        <Suspense
                            fallback={
                                <Center py={8}>
                                    <VStack spacing={4}>
                                        <Spinner size="lg" color="blue.500" />
                                        <Text fontSize="sm" color="whiteAlpha.700">
                                            Loading call...
                                        </Text>
                                    </VStack>
                                </Center>
                            }
                        >
                            <CallRequestUpsertContainer
                                as={Components}
                                {...props.content}
                            />
                        </Suspense>
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    };
}
